import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_PLANSCOUT_API_URL || 'http://localhost:8080';

/**
 * Create axios instance with authorization header
 * @param token - Auth0 access token
 */
const createAuthorizedClient = (token: string): AxiosInstance => {
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export interface UserPermissions {
  canViewAllClients: boolean;
  canManageUsers: boolean;
  canViewDashboard: boolean;
  canEditClients: boolean;
  canManagePlanRequests: boolean;
}

/**
 * Enterprise org scope: a firm (orgUnitId null/absent) or one of its
 * branches/teams. Users may hold several; any non-planner with at least one
 * gets the enterprise view.
 */
export interface OrgAssignment {
  firmId: string;
  orgUnitId?: string | null;
}

export interface User {
  _id?: string;
  auth0UserId: string;
  email: string;
  name: string;
  role: 'superadmin' | 'dashboard-admin' | 'dashboard-advisor' | 'planner';
  assignedCustomerIds: string[];
  orgAssignments?: OrgAssignment[];
  permissions: UserPermissions;
  showPlanScoutLogo?: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

/**
 * Fetch current user's information from the backend
 * @param token - Auth0 access token
 */
export const getCurrentUser = async (token: string): Promise<User | null> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get('/api/users/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

/**
 * Self-service update of the current user's own display name (the login
 * account — independent of any advisor configuration). Syncs to Auth0
 * server-side; returns the updated user (with `auth0SyncWarning` if the
 * Auth0 patch failed).
 */
export const updateMyName = async (
  token: string,
  name: string
): Promise<User & { auth0SyncWarning?: string }> => {
  const client = createAuthorizedClient(token);
  const response = await client.put('/api/users/me', { name });
  return response.data;
};

/**
 * The advisor's previous dashboard login (session start before the current
 * one). Null when they've only ever had one session. Used by the Home
 * "New views" indicator; never throws (returns null on error).
 */
export const getPreviousLoginAt = async (token: string): Promise<string | null> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get('/api/me/previous-login');
    return response.data?.previousLoginAt ?? null;
  } catch (error) {
    console.warn('Error fetching previous login:', error);
    return null;
  }
};

/**
 * Fetch all users (admin only)
 * @param token - Auth0 access token
 */
export const getAllUsers = async (token: string): Promise<User[]> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get('/api/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

/**
 * Create a new user (admin only)
 * @param token - Auth0 access token
 */
export const createUser = async (token: string, userData: Omit<User, '_id' | 'createdAt' | 'updatedAt'>): Promise<User> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.post('/api/users', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update an existing user (admin only)
 * @param token - Auth0 access token
 */
export const updateUser = async (token: string, userId: string, updates: Partial<User>): Promise<User> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.put(`/api/users/${userId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Delete a user (superadmin only)
 * @param token - Auth0 access token
 */
export const deleteUser = async (token: string, userId: string): Promise<void> => {
  try {
    const client = createAuthorizedClient(token);
    await client.delete(`/api/users/${userId}`);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Get user by ID
 * @param token - Auth0 access token
 */
export const getUserById = async (token: string, userId: string): Promise<User> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user by ID:', error);
    throw error;
  }
};

/**
 * Auth0 user data returned from Management API
 */
export interface Auth0User {
  auth0UserId: string;
  email: string;
  name: string;
  picture?: string;
  emailVerified: boolean;
}

/**
 * Fetch user data from Auth0 by auth0UserId (admin only)
 * @param token - Auth0 access token
 * @param auth0UserId - Auth0 user ID (e.g., auth0|123456789)
 */
export const fetchUserFromAuth0 = async (token: string, auth0UserId: string): Promise<Auth0User> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get(`/api/users/auth0/${encodeURIComponent(auth0UserId)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user from Auth0:', error);
    throw error;
  }
};

export interface CreateUserWithAuth0Data {
  email: string;
  name: string;
  role: User['role'];
  assignedCustomerIds: string[];
  orgAssignments?: OrgAssignment[];
  auth0RoleIds?: string[];
}

export interface CreateUserWithAuth0Response extends User {
  passwordEmailSent: boolean;
  auth0RolesAssigned: boolean;
}

export interface Auth0Role {
  id: string;
  name: string;
  description: string;
}

/**
 * Create a new user with automatic Auth0 account creation (admin only)
 * Creates the Auth0 account, MongoDB record, and sends password setup email
 */
export const createUserWithAuth0 = async (token: string, userData: CreateUserWithAuth0Data): Promise<CreateUserWithAuth0Response> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.post('/api/users/create-with-auth0', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user with Auth0:', error);
    throw error;
  }
};

/**
 * Send password reset email for a user (admin only)
 */
export const resetUserPassword = async (token: string, userId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.post(`/api/users/${userId}/reset-password`);
    return response.data;
  } catch (error) {
    console.error('Error resetting user password:', error);
    throw error;
  }
};

/**
 * Fetch all available Auth0 roles (admin only)
 */
export const getAuth0Roles = async (token: string): Promise<Auth0Role[]> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get('/api/auth0/roles');
    return response.data;
  } catch (error) {
    console.error('Error fetching Auth0 roles:', error);
    throw error;
  }
};

/**
 * Get Auth0 roles assigned to a specific user (admin only)
 */
export const getUserAuth0Roles = async (token: string, userId: string): Promise<Auth0Role[]> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get(`/api/users/${userId}/auth0-roles`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user Auth0 roles:', error);
    throw error;
  }
};

/**
 * Set Auth0 roles for a user (admin only)
 */
export const setUserAuth0Roles = async (token: string, userId: string, roleIds: string[]): Promise<void> => {
  try {
    const client = createAuthorizedClient(token);
    await client.put(`/api/users/${userId}/auth0-roles`, { roleIds });
  } catch (error) {
    console.error('Error setting user Auth0 roles:', error);
    throw error;
  }
};
